import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import ImageUploader from '../components/imageUploader';
import Spinner from '../components/Spinner/spinner';
import { getSgrData, updateSgrData, SgrData } from '../services/SgrService';
import { routes } from '../routes/routePaths';
import { useToast } from '../components/toast/toast';

const Sgr = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<SgrData>({
    logo: '',
    name: '',
    cuit: '',
    address: '',
    location: '',
    email: '',
    legalRepresentativeName: '',
    legalRepresentativeDni: '',
    legalRepresentativeCuit: '',
    legalRepresentativeEmail: '',
  });

  useEffect(() => {
    fetchSgrData();
  }, []);

  const fetchSgrData = async () => {
    setLoading(true);
    try {
      const response = await getSgrData();
      if (response.success && response.data) {
        setFormData(response.data);
      } else {
        useToast.error({
          title: response.message || 'Error al cargar los datos',
          id: 'load-error-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch (error) {
      useToast.error({
        title: 'Error al cargar los datos',
        id: 'load-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleInputChange = (field: keyof SgrData) => (e: any) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleImageLoad = (_file: File, imageUrl: string) => {
    setFormData({
      ...formData,
      logo: imageUrl,
    });
  };

  const handleImageError = (error: string) => {
    useToast.error({
      title: error,
      id: 'image-error-toast',
      description: '',
      closeButton: true,
      autoClose: 5000,
    });
  };

  const handleSave = async () => {
    // Validaciones básicas
    if (!formData.name || !formData.email || !formData.address || !formData.location) {
      useToast.error({
        title: 'Por favor complete todos los campos obligatorios de la empresa',
        id: 'validation-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    if (!formData.legalRepresentativeName || !formData.legalRepresentativeDni || 
        !formData.legalRepresentativeCuit || !formData.legalRepresentativeEmail) {
      useToast.error({
        title: 'Por favor complete todos los campos del representante legal',
        id: 'validation-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    setSaving(true);
    try {
      const response = await updateSgrData(formData);
      if (response.success) {
        useToast.success({
          title: 'Datos guardados correctamente',
          id: 'save-success-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      } else {
        useToast.error({
          title: response.message || 'Error al guardar los datos',
          id: 'save-error-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch (error) {
      useToast.error({
        title: 'Error al guardar los datos',
        id: 'save-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
        <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '3' }}>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a configuración"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Mis datos de empresa
          </H>
        </Card>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <Spinner size='lg' />
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
      {/* Header */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Mis datos de empresa
        </H>
      </Card>

      {/* Primera Sección - Logo y datos de empresa */}
      <Card 
        spacing='4'
        margin={{ bottom: '3' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '25% 1fr', gap: '24px' }}>
          {/* Logo */}
          <div>

            <ImageUploader
              acceptedFormats={['.svg']}
              maxSizeKB={200}
              initialImage={formData.logo}
              onImageLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>

          {/* Datos de empresa */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Fila 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Input
                label='Nombre'
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder='Nombre de la empresa'
                size='md'
              />
              <Input
                label='Email'
                type='email'
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder='email@empresa.com'
                size='md'
              />
            </div>

            {/* Fila 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Input
                label='Dirección completa'
                value={formData.address}
                onChange={handleInputChange('address')}
                placeholder='Calle, Número, Piso, Depto'
                size='md'
              />
              <Input
                label='Locación'
                value={formData.location}
                onChange={handleInputChange('location')}
                placeholder='Ciudad, Provincia'
                size='md'
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Segunda Sección - Representante Legal */}
      <Card 
        spacing='4'
        margin={{ bottom: '3' }}
      >
        <Card margin={{ bottom: '3' }}>
          <H withMargin={false} type='2' weight='bold' modifier='strong'>
            Representante legal
          </H>
        </Card>

        {/* Fila de 4 inputs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <Input
            label='Nombre y Apellido'
            value={formData.legalRepresentativeName}
            onChange={handleInputChange('legalRepresentativeName')}
            placeholder='Juan Pérez'
            size='md'
          />
          <Input
            label='CUIT'
            value={formData.legalRepresentativeCuit}
            onChange={handleInputChange('legalRepresentativeCuit')}
            placeholder='20-12345678-9'
            size='md'
          />
          <Input
            label='DNI'
            value={formData.legalRepresentativeDni}
            onChange={handleInputChange('legalRepresentativeDni')}
            placeholder='12.345.678'
            size='md'
          />
          <Input
            label='Email'
            type='email'
            value={formData.legalRepresentativeEmail}
            onChange={handleInputChange('legalRepresentativeEmail')}
            placeholder='email@representante.com'
            size='md'
          />
        </div>
      </Card>

      {/* Botón Guardar */}
      <Card display='flex' justifyContent='flex-end'>
        <Button
          variant='primary'
          size='lg'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </Card>
    </Card>
  );
};

export default Sgr;
